/**
 * Slot Integration API Service
 *
 * Stateless API functions for the integration API at https://www.integration-api.net/games/
 * Follows existing BackendGameApiService.ts pattern (plain fetch()).
 */

import type {
  DemoTokenResponse,
  LastActivityResponse,
  BetResponse,
  BetResponseData,
  ResultResponse,
  BuyFeatureResponse,
  CheckBalanceResponse,
  NormalizedBetResult,
  SlotApiSession,
} from '@core/types/slot-api.types';

const INTEGRATION_API_BASE = 'https://www.integration-api.net/games/';

const BET_RETRY_MAX = 5;
const BET_RETRY_DELAY_MS = 500;

export async function requestDemoToken(gameId: string): Promise<DemoTokenResponse> {
  const res = await fetch(`${INTEGRATION_API_BASE}play-demo/${gameId}`);
  if (!res.ok) throw new Error(`Failed to get demo token: ${res.status}`);
  return res.json();
}

export async function requestLastActivity(token: string): Promise<LastActivityResponse> {
  const res = await fetch(`${INTEGRATION_API_BASE}last-activity?token=${encodeURIComponent(token)}`);
  if (!res.ok) throw new Error(`Failed to get last activity: ${res.status}`);
  return res.json();
}

/**
 * Request a bet/spin from the API.
 * @param token - Session token
 * @param betIndex - Index into the betConfig array (0-based), NOT the actual bet value.
 *                   The backend resolves the actual amount from its own bet_config.
 */
export async function requestBet(token: string, betIndex: number): Promise<BetResponse> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < BET_RETRY_MAX; attempt++) {
    try {
      const res = await fetch(`${INTEGRATION_API_BASE}bet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, amount: betIndex }),
      });

      const json: BetResponse = await res.json();

      // BetOnProcessSettlement: previous round wasn't settled (e.g. user force-stopped).
      // Auto-settle by calling /result, then retry the bet.
      if (json.error?.code === 'BetOnProcessSettlement') {
        lastError = new Error(json.error.message || 'BetOnProcessSettlement');
        try {
          console.warn('[SlotApi] BetOnProcessSettlement — auto-settling previous round via /result');
          await requestResult(token);
        } catch (settleErr) {
          console.error('[SlotApi] Auto-settle /result failed:', (settleErr as Error).message);
        }
        await new Promise((resolve) => setTimeout(resolve, BET_RETRY_DELAY_MS));
        continue;
      }

      if (!res.ok && !json.data) {
        throw new Error(`Bet request failed: ${res.status}`);
      }

      return json;
    } catch (err) {
      lastError = err as Error;
      if (attempt < BET_RETRY_MAX - 1) {
        await new Promise((resolve) => setTimeout(resolve, BET_RETRY_DELAY_MS));
      }
    }
  }

  throw lastError || new Error('Bet request failed after retries');
}

export async function requestResult(token: string): Promise<ResultResponse> {
  const res = await fetch(`${INTEGRATION_API_BASE}result`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new Error(`Failed to get result: ${res.status}`);
  return res.json();
}

export async function requestBuyFeature(
  token: string,
  amount: number,
  selectedFeature: number
): Promise<BuyFeatureResponse> {
  const res = await fetch(`${INTEGRATION_API_BASE}buy-feature`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, amount, selected_feature: selectedFeature }),
  });
  if (!res.ok) throw new Error(`Failed to buy feature: ${res.status}`);
  return res.json();
}

export async function checkBalance(token: string): Promise<CheckBalanceResponse> {
  const res = await fetch(
    `${INTEGRATION_API_BASE}check-balance?token=${encodeURIComponent(token)}`
  );
  if (!res.ok) throw new Error(`Failed to check balance: ${res.status}`);
  return res.json();
}

/**
 * Normalize a bet response to a format-agnostic result.
 * Prefers compact format (crl/ws/dtl) → falls back to legacy format.
 * Returns a safe default (endRound=true) when data is null/undefined
 * (e.g. API error responses).
 */
export function normalizeBetResponse(data: BetResponseData | null | undefined): NormalizedBetResult {
  if (!data) {
    return {
      currentReel: [],
      winningPositions: [],
      winReward: [],
      spinWin: 0,
      totalWin: 0,
      endRound: true,
      balance: 0,
      multiplier: 0,
      freeSpinCount: 0,
      mode: 0,
      isBonusSpin: false,
    };
  }

  const currentReel = data.crl ?? data.current_reel ?? [];
  const winningPositions = data.ws ?? data.winning_position ?? [];
  const winReward = data.wr ?? data.winning_reward ?? [];
  const spinWin = data.sw ?? data.spin_win ?? 0;
  const totalWin = data.tw ?? data.total_win ?? 0;
  const mode = data.mod ?? data.mode ?? 0;
  const multiplier = data.dtl?.mult ?? data.details?.mult ?? 0;
  const freeSpinCount = data.dtl?.frm ?? data.details?.frm ?? 0;
  const isBonusSpin = data.dtl?.is_bonus_spin ?? data.details?.is_bonus_spin ?? false;

  return {
    currentReel,
    winningPositions,
    winReward,
    spinWin,
    totalWin,
    endRound: data.end_round ?? true,
    balance: data.balance,
    multiplier,
    freeSpinCount,
    mode,
    isBonusSpin,
  };
}

/**
 * Initialize a full API session: get demo token → fetch last activity → assemble session.
 */
export async function initSession(gameId: string): Promise<SlotApiSession> {
  const tokenRes = await requestDemoToken(gameId);

  if (tokenRes.error?.code) {
    throw new Error(`Demo token error: ${tokenRes.error.message}`);
  }

  const token = tokenRes.data.token;
  const expiresIn = tokenRes.data.expires_in;

  const activityRes = await requestLastActivity(token);

  if (activityRes.error?.code) {
    throw new Error(`Last activity error: ${activityRes.error.message}`);
  }

  return {
    token,
    expiresAt: Date.now() + expiresIn * 1000,
    gameId,
    balance: activityRes.data.balance,
    currency: activityRes.data.currency,
    betConfig: activityRes.data.bet_config,
    lastBet: activityRes.data.last_bet,
    bonusEvent: activityRes.data.bonus_event,
    lastActivity: activityRes.data.last_activity ?? null,
  };
}
